/**
 * The timber pavilion: a small East Asian post-and-beam room. The structure is
 * the decoration — corner and mid-wall posts, a two-layer beam lattice under a
 * warm laylight, a low timber border round the floor — and the art is hung
 * sparsely, one calm eye-level row per bay between the posts.
 *
 * Its one ceremonial wall is a tokonoma: a shallow alcove made of two proud
 * fins, a head beam and a raised board, holding a single hero piece the way an
 * alcove holds a hanging scroll.
 */

import { PALACE, SIDES } from '../constants';
import { rectSegments } from '../hang';

const POST_W = 0.3; // post width, square in plan
const POST_BITE = 0.06; // how far a post buries into the wall (no coplanar faces)
const ALC_W = 2.9; // preferred clear width of the alcove
const ALC_D = 0.62; // how far the alcove fins stand proud of the wall
const ALC_FIN = 0.22; // fin width
const ALC_H = 3.15; // underside of the alcove head beam
const BORDER_W = 0.75; // raised timber floor border
const BORDER_H = 0.07;

const interiorOf = (node) => ({
  x1: node.x1 + PALACE.WALL_T,
  x2: node.x2 - PALACE.WALL_T,
  z1: node.z1 + PALACE.WALL_T,
  z2: node.z2 - PALACE.WALL_T,
});

const isHorizontal = (side) => side === 'N' || side === 'S';

// The interior face coordinate of a wall, and the sign that points inward.
const faceOf = (node, side) => {
  const I = interiorOf(node);
  if (side === 'N') return { face: I.z2, inward: -1 };
  if (side === 'S') return { face: I.z1, inward: 1 };
  if (side === 'E') return { face: I.x2, inward: -1 };
  return { face: I.x1, inward: 1 };
};

// The strip of floor each door needs in front of it, as a world-space rect.
// Nothing solid may stand in one — including things hugging a *different*
// wall that reach around a corner into the approach.
const doorCorridors = (node, pad, depth = 3.2) =>
  (node.doors || []).map((d) => {
    const half = (d.w || PALACE.DOOR_W) / 2 + pad(d);
    if (d.side === 'N') return [d.at - half, node.z2 - depth, d.at + half, node.z2];
    if (d.side === 'S') return [d.at - half, node.z1, d.at + half, node.z1 + depth];
    if (d.side === 'E') return [node.x2 - depth, d.at - half, node.x2, d.at + half];
    return [node.x1, d.at - half, node.x1 + depth, d.at + half];
  });

const rectsOverlap = (a, b) => a[0] < b[2] && b[0] < a[2] && a[1] < b[3] && b[1] < a[3];

// Runs along a wall face where a solid `dep` deep would clear every door —
// the one on this wall and any reaching round a corner from another.
const clearRuns = (node, side, dep, samePad = 0.9, otherPad = 0.55) => {
  const I = interiorOf(node);
  const horizontal = isHorizontal(side);
  const { face, inward } = faceOf(node, side);
  const c1 = Math.min(face, face + inward * dep);
  const c2 = Math.max(face, face + inward * dep);
  const lo = (horizontal ? I.x1 : I.z1) + 0.45;
  const hi = (horizontal ? I.x2 : I.z2) - 0.45;

  const blocks = doorCorridors(node, (d) => (d.side === side ? samePad : otherPad))
    .filter((cor) => (horizontal ? cor[1] < c2 && c1 < cor[3] : cor[0] < c2 && c1 < cor[2]))
    .map((cor) => (horizontal ? [cor[0], cor[2]] : [cor[1], cor[3]]))
    .sort((a, b) => a[0] - b[0]);

  const runs = [];
  let cursor = lo;
  for (const [a, b] of blocks) {
    if (a - cursor > 0.2) runs.push([cursor, Math.min(a, hi)]);
    cursor = Math.max(cursor, b);
  }
  if (hi - cursor > 0.2) runs.push([cursor, hi]);
  return runs.filter(([a, b]) => b - a > 0.2);
};

// Which short wall carries the tokonoma, and where. Pure in `node` so that
// segments() and build() always agree.
const alcoveOf = (node) => {
  const w = node.x2 - node.x1;
  const d = node.z2 - node.z1;
  const shortSides = w <= d ? ['N', 'S'] : ['E', 'W'];
  let best = null;
  for (const side of shortSides) {
    const doorCount = (node.doors || []).filter((x) => x.side === side).length;
    for (const [r1, r2] of clearRuns(node, side, ALC_D + 0.1)) {
      const len = r2 - r1;
      if (len < 2.4) continue;
      const score = len - doorCount * 0.75;
      if (!best || score > best.score) {
        best = {
          side,
          at: (r1 + r2) / 2,
          width: Math.min(ALC_W, len - 0.5),
          score,
        };
      }
    }
  }
  return best;
};

// Mid-wall post positions per side (world along-coordinates): one at the wall
// centre, dropped when a door or the alcove already claims that spot.
const midPosts = (node) => {
  const I = interiorOf(node);
  const alc = alcoveOf(node);
  const out = {};
  const corridors = doorCorridors(node, () => 0.5);
  for (const side of ['N', 'S', 'E', 'W']) {
    const horizontal = isHorizontal(side);
    const at = horizontal ? (I.x1 + I.x2) / 2 : (I.z1 + I.z2) / 2;
    const { face, inward } = faceOf(node, side);
    const c1 = Math.min(face - inward * POST_BITE, face + inward * POST_W);
    const c2 = Math.max(face - inward * POST_BITE, face + inward * POST_W);
    const rect = horizontal
      ? [at - POST_W / 2 - 0.4, c1, at + POST_W / 2 + 0.4, c2]
      : [c1, at - POST_W / 2 - 0.4, c2, at + POST_W / 2 + 0.4];
    const blocked = corridors.some((cor) => rectsOverlap(rect, cor));
    const inAlcove =
      alc && alc.side === side && Math.abs(alc.at - at) < alc.width / 2 + ALC_FIN + 0.3;
    out[side] = blocked || inAlcove ? [] : [at];
  }
  return out;
};

// Split a hangable run wherever a post interrupts it, so pictures hang in the
// bays between the timbers rather than through them.
const carve = (seg, cuts) => {
  const out = [];
  let cursor = 0;
  for (const [a, b] of cuts.slice().sort((p, q) => p[0] - q[0])) {
    if (b <= 0 || a >= seg.len) continue;
    if (Math.min(a, seg.len) - cursor >= 1.1) out.push([cursor, Math.min(a, seg.len)]);
    cursor = Math.max(cursor, b);
  }
  if (seg.len - cursor >= 1.1) out.push([cursor, seg.len]);
  return out.map(([a, b]) => ({
    ...seg,
    ox: seg.ox + seg.dx * a,
    oz: seg.oz + seg.dz * a,
    len: b - a,
    style: b - a < 2.4 ? 'hero' : 'row',
  }));
};

const pavilion = {
  id: 'pavilion',
  role: 'wing',
  weight: 2,

  plan(rand, { axis }) {
    const long = 10 + rand() * 2;
    const cross = 9 + rand();
    return {
      w: axis === 'x' ? long : cross,
      d: axis === 'x' ? cross : long,
      height: 6,
    };
  },

  segments(node) {
    const alc = alcoveOf(node);
    const posts = midPosts(node);
    const base = rectSegments(node, { longStyle: 'row', shortStyle: 'row' });
    const alcN = alc ? SIDES[alc.side] : null;

    const out = [];
    for (const seg of base) {
      // Drop the alcove wall entirely — the alcove speaks for it.
      if (alcN && seg.nx === -alcN.dx && seg.nz === -alcN.dz) continue;
      // Which side is this run on? Match the inward normal.
      const side = ['N', 'S', 'E', 'W'].find(
        (s) => seg.nx === -SIDES[s].dx && seg.nz === -SIDES[s].dz
      );
      const cuts = (posts[side] || []).map((p) => {
        const local = seg.dx ? p - seg.ox : p - seg.oz;
        return [local - POST_W / 2 - 0.3, local + POST_W / 2 + 0.3];
      });
      out.push(...carve(seg, cuts));
    }

    if (alc) {
      const { face } = faceOf(node, alc.side);
      const n = SIDES[alc.side];
      const horizontal = isHorizontal(alc.side);
      out.push({
        ox: horizontal ? alc.at - alc.width / 2 : face,
        oz: horizontal ? face : alc.at - alc.width / 2,
        dx: horizontal ? 1 : 0,
        dz: horizontal ? 0 : 1,
        nx: -n.dx,
        nz: -n.dz,
        len: alc.width,
        y0: 0.95,
        y1: 3.0,
        style: 'hero',
      });
    }
    return out.sort((a, b) => b.len - a.len);
  },

  build(node, T) {
    const height = node.height;
    const { group, colliders } = T.standardShell(node, T.kit, {
      laylight: false, // the beam lattice frames its own light panel
      corniceH: 0.28,
    });
    const I = interiorOf(node);
    const timber = [];

    // --- raised floor border. N/S runs take the corners, E/W stop short, so
    // no two border tops share a plane. Only 7cm proud: a lip to step over,
    // not an obstacle, so it carries no collider.
    timber.push(
      T.box(I.x1 - POST_BITE, I.x2 + POST_BITE, -0.02, BORDER_H, I.z1 - POST_BITE, I.z1 + BORDER_W),
      T.box(I.x1 - POST_BITE, I.x2 + POST_BITE, -0.02, BORDER_H, I.z2 - BORDER_W, I.z2 + POST_BITE),
      T.box(I.x1 - POST_BITE, I.x1 + BORDER_W, -0.02, BORDER_H, I.z1 + BORDER_W, I.z2 - BORDER_W),
      T.box(I.x2 - BORDER_W, I.x2 + POST_BITE, -0.02, BORDER_H, I.z1 + BORDER_W, I.z2 - BORDER_W)
    );

    // --- corner posts, buried a little into both walls they meet
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        const x = sx < 0 ? I.x1 : I.x2;
        const z = sz < 0 ? I.z1 : I.z2;
        const x1 = sx < 0 ? x - POST_BITE : x - POST_W;
        const x2 = sx < 0 ? x + POST_W : x + POST_BITE;
        const z1 = sz < 0 ? z - POST_BITE : z - POST_W;
        const z2 = sz < 0 ? z + POST_W : z + POST_BITE;
        timber.push(T.box(x1, x2, 0, height + 0.05, z1, z2));
        colliders.push({ rect: [x1, z1, x2, z2], top: height });
      }
    }

    // --- mid-wall posts
    const posts = midPosts(node);
    for (const side of ['N', 'S', 'E', 'W']) {
      const { face, inward } = faceOf(node, side);
      const horizontal = isHorizontal(side);
      for (const at of posts[side]) {
        const p1 = face - inward * POST_BITE;
        const p2 = face + inward * POST_W;
        const a1 = at - POST_W / 2;
        const a2 = at + POST_W / 2;
        timber.push(
          horizontal
            ? T.box(a1, a2, 0, height + 0.05, p1, p2)
            : T.box(p1, p2, 0, height + 0.05, a1, a2)
        );
        colliders.push({
          rect: horizontal
            ? [a1, Math.min(p1, p2), a2, Math.max(p1, p2)]
            : [Math.min(p1, p2), a1, Math.max(p1, p2), a2],
          top: height,
        });
      }
    }

    // --- beam lattice: main beams along the long axis, cross beams a course
    // below, overlapping by 2cm so no two beam faces are coplanar.
    const longX = I.x2 - I.x1 >= I.z2 - I.z1;
    const lx1 = I.x1 + 0.45;
    const lx2 = I.x2 - 0.45;
    const lz1 = I.z1 + 0.45;
    const lz2 = I.z2 - 0.45;
    const BEAM = 0.16;
    const mainY = [height - 0.3, height - 0.04];
    const crossY = [height - 0.56, height - 0.28];
    const spread = (a1, a2, step) => {
      const n = Math.max(2, Math.round((a2 - a1) / step));
      const out = [];
      for (let i = 0; i <= n; i++) out.push(a1 + ((a2 - a1) * i) / n);
      return out;
    };
    for (const z of spread(lz1, lz2, longX ? 1.7 : 1.5)) {
      const y = longX ? crossY : mainY;
      timber.push(T.box(lx1, lx2, y[0], y[1], z - BEAM / 2, z + BEAM / 2));
    }
    for (const x of spread(lx1, lx2, longX ? 1.5 : 1.7)) {
      const y = longX ? mainY : crossY;
      timber.push(T.box(x - BEAM / 2, x + BEAM / 2, y[0], y[1], lz1, lz2));
    }

    // --- the tokonoma
    const alc = alcoveOf(node);
    if (alc) {
      const { face, inward } = faceOf(node, alc.side);
      const horizontal = isHorizontal(alc.side);
      const slab = (a1, a2, y0, y1, d0, d1) => {
        const p1 = face + inward * d0;
        const p2 = face + inward * d1;
        timber.push(
          horizontal ? T.box(a1, a2, y0, y1, p1, p2) : T.box(p1, p2, y0, y1, a1, a2)
        );
        return {
          rect: horizontal
            ? [a1, Math.min(p1, p2), a2, Math.max(p1, p2)]
            : [Math.min(p1, p2), a1, Math.max(p1, p2), a2],
          top: y1,
        };
      };
      const half = alc.width / 2;
      for (const s of [-1, 1]) {
        const a = alc.at + s * (half + ALC_FIN / 2);
        colliders.push(slab(a - ALC_FIN / 2, a + ALC_FIN / 2, 0, ALC_H, -0.05, ALC_D));
      }
      // head beam (above head height — no collider needed)
      slab(alc.at - half - ALC_FIN, alc.at + half + ALC_FIN, ALC_H, ALC_H + 0.32, -0.05, ALC_D);
      // raised display board
      colliders.push(slab(alc.at - half, alc.at + half, 0, 0.2, -0.05, 0.55));
    }

    group.add(T.mergedMesh(timber, T.kit.materials.timber));

    // --- warm light panel inside the lattice
    const panel = new T.THREE.Mesh(T.kit.geometries.unitPlane, T.kit.materials.laylight);
    panel.rotation.x = Math.PI / 2;
    panel.scale.set(Math.max(1, lx2 - lx1 - 0.6), Math.max(1, lz2 - lz1 - 0.6), 1);
    panel.position.set((lx1 + lx2) / 2, height - 0.02, (lz1 + lz2) / 2);
    panel.matrixAutoUpdate = false;
    panel.updateMatrix();
    group.add(panel);

    // A single bench, set off-centre — but only where it stays out of every
    // door's approach.
    const corridors = doorCorridors(node, () => 0.55);
    const rotY = longX ? 0 : Math.PI / 2;
    const along = longX ? 2.2 : 0.55;
    const across = longX ? 0.55 : 2.2;
    for (const s of [1, -1, 0]) {
      const bx = (I.x1 + I.x2) / 2 + (longX ? 0 : s * 1.5);
      const bz = (I.z1 + I.z2) / 2 + (longX ? s * 1.5 : 0);
      const rect = [bx - along / 2 - 0.4, bz - across / 2 - 0.4, bx + along / 2 + 0.4, bz + across / 2 + 0.4];
      if (corridors.some((cor) => rectsOverlap(rect, cor))) continue;
      const b = T.bench(T.kit, bx, bz, rotY, 2.2);
      group.add(b.mesh);
      colliders.push(b.collider);
      break;
    }

    return { group, colliders };
  },
};

export default pavilion;
